import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';

import { Button } from 'components/ui/button';
import { Card, CardContent } from 'components/ui/card';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormMessage } from 'components/ui/form';
import { useToast } from 'hooks/use-toast';
import {
  useConnectTool,
  useDisconnectTool,
  useGetToolConnection,
  useStartToolOidc,
} from 'features/chat/agent-chat-module/hooks/use-gpt-chat';
import {
  OwnToolClientSection,
  serverErrorCode,
  serverErrorText,
  USER_CLIENT_REQUIRED,
} from 'features/chat/components/OwnToolClientSection';

interface ToolConnectionCardProps {
  toolId: string;
  toolName: string;
  subType: 'password' | 'oidc';
}

interface CredsFormData {
  username: string;
  password: string;
}

/**
 * Per-user connection panel for an "auto" tool: shows token-cache status and lets the
 * current user connect (password → creds modal; oidc → IdP popup) / disconnect. Where the
 * tool's owner allows it, the user also manages their own client here (OwnToolClientSection).
 */
export function ToolConnectionCard({
  toolId,
  toolName,
  subType,
}: ToolConnectionCardProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: connection, isLoading } = useGetToolConnection(toolId);
  const { mutateAsync: connectTool, isPending: isConnecting } = useConnectTool();
  const { mutateAsync: disconnectTool, isPending: isDisconnecting } = useDisconnectTool();
  const { mutateAsync: startOidc, isPending: isStartingOidc } = useStartToolOidc();

  const [credsOpen, setCredsOpen] = useState(false);
  // Set when sign-in answers that the user has to bring their own client first.
  const [userClientRequired, setUserClientRequired] = useState(false);

  const form = useForm<CredsFormData>({ mode: 'onChange', defaultValues: { username: '', password: '' } });
  const { control, handleSubmit, reset } = form;

  useEffect(() => {
    if (credsOpen) reset({ username: '', password: '' });
  }, [credsOpen, reset]);

  const refetchConnection = () =>
    queryClient.invalidateQueries({ queryKey: ['tools', 'connection', toolId] });

  const connected = !!connection?.connected;

  const onConnectPassword = async (data: CredsFormData) => {
    try {
      const response = await connectTool({
        toolId,
        payload: { username: data.username.trim(), password: data.password },
      });
      if (!response.connected) {
        throw new Error(response.error || t('INVALID_CREDENTIALS') || 'Invalid credentials');
      }
      await refetchConnection();
      setCredsOpen(false);
      toast({ variant: 'success', title: t('CONNECTED') || 'Connected' });
    } catch (error) {
      if (serverErrorCode(error) === USER_CLIENT_REQUIRED) {
        setCredsOpen(false);
        setUserClientRequired(true);
        return;
      }
      toast({
        variant: 'destructive',
        title: t('CONNECTION_FAILED') || 'Connection failed',
        description:
          serverErrorText(error) ||
          (error instanceof Error ? error.message : t('INVALID_CREDENTIALS') || 'Invalid credentials.'),
      });
    }
  };

  const onConnectOidc = async () => {
    try {
      const { authorizationUrl } = await startOidc({ toolId });
      const popup = window.open(authorizationUrl, 'mcp-oauth', 'width=520,height=680');
      const onMessage = (event: MessageEvent) => {
        const msg = event.data as
          | { type?: string; ok?: boolean; toolId?: string; error?: string }
          | undefined;
        if (msg?.type !== 'mcp-oauth') return;
        if (msg.toolId && msg.toolId !== toolId) return;
        window.removeEventListener('message', onMessage);
        if (!msg.ok) {
          toast({
            variant: 'destructive',
            title: t('CONNECTION_FAILED') || 'Connection failed',
            description: msg.error || t('SOMETHING_WENT_WRONG') || 'Something went wrong.',
          });
          return;
        }
        void refetchConnection();
        toast({ variant: 'success', title: t('CONNECTED') || 'Connected' });
      };
      window.addEventListener('message', onMessage);
      if (!popup) {
        window.removeEventListener('message', onMessage);
        toast({
          variant: 'destructive',
          title: t('POPUP_BLOCKED') || 'Popup blocked',
          description: t('ALLOW_POPUPS_TO_CONNECT') || 'Please allow popups to connect.',
        });
      }
    } catch (error) {
      if (serverErrorCode(error) === USER_CLIENT_REQUIRED) {
        setUserClientRequired(true);
        return;
      }
      toast({
        variant: 'destructive',
        title: t('CONNECTION_FAILED') || 'Connection failed',
        description:
          serverErrorText(error) ||
          (error instanceof Error ? error.message : t('SOMETHING_WENT_WRONG') || 'Something went wrong.'),
      });
    }
  };

  const onDisconnect = async () => {
    try {
      await disconnectTool({ toolId });
      await refetchConnection();
      toast({ variant: 'success', title: t('DISCONNECTED') || 'Disconnected' });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('ACTION_FAILED') || 'Action failed',
        description: error instanceof Error ? error.message : t('SOMETHING_WENT_WRONG') || 'Something went wrong.',
      });
    }
  };

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <h3 className="text-sm font-medium">{t('TOOL_SESSION') || 'Session'}</h3>
            <p className="text-sm text-muted-foreground">
              {t('TOOL_SESSION_DESCRIPTION') ||
                'Sign in to this service to use it in chat. Your credentials are used once to get an access token and are never stored.'}
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm">
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              ) : connected ? (
                <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
              ) : (
                <XCircle className="h-4 w-4 text-muted-foreground" />
              )}
              <span className={connected ? 'font-medium text-foreground' : 'text-muted-foreground'}>
                {isLoading
                  ? t('CHECKING') || 'Checking...'
                  : connected
                    ? t('CONNECTED') || 'Connected'
                    : t('NOT_CONNECTED') || 'Not connected'}
              </span>
              {connected && connection?.hasRefresh ? (
                <span className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                  {t('AUTO_REFRESH') || 'Auto-refresh'}
                </span>
              ) : null}
            </div>

            <div className="flex gap-2">
              {connected ? (
                <Button variant="outline" onClick={onDisconnect} disabled={isDisconnecting}>
                  {isDisconnecting ? t('DISCONNECTING') || 'Disconnecting...' : t('DISCONNECT') || 'Disconnect'}
                </Button>
              ) : subType === 'oidc' ? (
                <Button onClick={onConnectOidc} disabled={isStartingOidc}>
                  {isStartingOidc ? t('CONNECTING') || 'Connecting...' : t('CONNECT') || 'Connect'}
                </Button>
              ) : (
                <Button onClick={() => setCredsOpen(true)}>{t('CONNECT') || 'Connect'}</Button>
              )}
            </div>
          </div>

          <OwnToolClientSection
            toolId={toolId}
            required={userClientRequired}
            onChange={() => setUserClientRequired(false)}
          />
        </div>
      </CardContent>

      <Dialog open={credsOpen} onOpenChange={setCredsOpen}>
        <DialogContent className="max-w-[440px] rounded-[0.5rem] p-4">
          <DialogHeader className="space-y-1">
            <DialogTitle className="text-lg font-medium leading-none">
              {t('CONNECT_TO_MCP_SERVICE', { name: toolName, defaultValue: 'Connect to {{name}}' }) || 'Connect'}
            </DialogTitle>
            <DialogDescription className="text-sm leading-6">
              {t('MCP_SERVICE_AUTH_REQUIRED_DESCRIPTION') ||
                'Your credentials are sent securely and never shared with the model.'}
            </DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={handleSubmit(onConnectPassword)} className="flex flex-col gap-4 py-2">
              <FormField
                control={control}
                name="username"
                rules={{ required: t('USERNAME_REQUIRED') || 'Username is required' }}
                render={({ field }) => (
                  <FormItem>
                    <Label className="text-sm font-medium leading-6">
                      {t('USERNAME') || 'Username'}
                      <span className="text-destructive ml-0.5">*</span>
                    </Label>
                    <FormControl>
                      <Input {...field} autoFocus className="h-10" placeholder={t('ENTER_USERNAME') || 'Enter username'} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="password"
                rules={{ required: t('PASSWORD_REQUIRED') || 'Password is required' }}
                render={({ field }) => (
                  <FormItem>
                    <Label className="text-sm font-medium leading-6">
                      {t('PASSWORD') || 'Password'}
                      <span className="text-destructive ml-0.5">*</span>
                    </Label>
                    <FormControl>
                      <Input {...field} type="password" className="h-10" placeholder={t('ENTER_PASSWORD') || 'Enter password'} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter className="gap-2 pt-2">
                <Button type="button" variant="ghost" onClick={() => setCredsOpen(false)} disabled={isConnecting}>
                  {t('CANCEL') || 'Cancel'}
                </Button>
                <Button type="submit" disabled={isConnecting}>
                  {isConnecting ? t('CONNECTING') || 'Connecting...' : t('CONNECT') || 'Connect'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default ToolConnectionCard;
